import React, { Suspense, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useLazyLoadQuery, useRelayEnvironment } from 'react-relay/hooks';

import { Grid, Paper, Table, TableBody, Toolbar } from '@material-ui/core';
import { GridProps } from '@material-ui/core/Grid';
import ChatIcon from '@material-ui/icons/Chat';
import FastRewindIcon from '@material-ui/icons/FastRewind';
import FeedbackIcon from '@material-ui/icons/Feedback';
import SubjectIcon from '@material-ui/icons/Subject';
import { graphql } from 'babel-plugin-relay/macro';
import { ErrorBoundary } from 'core';
import moment from 'moment';
import { fetchQuery } from 'relay-runtime';

import { useQueryOptions } from '@lib/hooks';

import {
  TableMessagesReceived,
  TableMessagesSent,
  TableMessagesUndelivered,
  TableReminders,
} from 'embeds/deprecated/fragments';

import {
  ButtonNavigateNext,
  ContentLoaderTableRows,
  EmptyStateTable,
  SvgIconWrapper,
  TableHeadMessages,
  tableHeadMessagesCols,
  TypographyWithIcon,
} from 'embeds/components';

import {
  MessagingActivityScreenRedirect,
  MessagingActivityScreenTab,
} from 'embeds/MessagingActivity/MessagingActivityScreen';

import { GridMessagingActivityTablesEmbedQuery } from './__generated__/GridMessagingActivityTablesEmbedQuery.graphql';

const messages = defineMessages({
  lastSent: {
    id: 'embeds.deprecated.queries.GridMessagingActivityTables.lastSent',
    defaultMessage: 'Last Sent Messages',
  },
  redirectSent: {
    id: 'embeds.deprecated.queries.GridMessagingActivityTables.redirectSent',
    defaultMessage: 'View all sent messages',
  },
  lastUndelivered: {
    id: 'embeds.deprecated.queries.GridMessagingActivityTables.lastUndelivered',
    defaultMessage: 'Last Undelivered Messages',
  },
  redirectUndelivered: {
    id: 'embeds.deprecated.queries.GridMessagingActivityTables.redirectUndelivered',
    defaultMessage: 'View all undelivered messages',
  },
  lastReceived: {
    id: 'embeds.deprecated.queries.GridMessagingActivityTables.lastReceived',
    defaultMessage: 'Last Patient Responses',
  },
  redirectReceived: {
    id: 'embeds.deprecated.queries.GridMessagingActivityTables.redirectReceived',
    defaultMessage: 'View all patient responses',
  },
  nextQueued: {
    id: 'embeds.deprecated.queries.GridMessagingActivityTables.nextQueued',
    defaultMessage: 'Next Reminders',
  },
  redirectQueued: {
    id: 'embeds.deprecated.queries.GridMessagingActivityTables.redirectQueued',
    defaultMessage: 'View all future reminders',
  },
  emptyStateNextReminders: {
    id: 'embeds.organisms.GridMessagingActivityTables.emptyStateNextReminders',
    defaultMessage: 'No future reminders yet',
  },
});

export interface GridMessagingActivityTablesProps extends Omit<GridProps, 'classes'> {
  dateFrom: number;
  sendAtFrom: number;
  dateTo: number;
  onRedirect: (redirect: MessagingActivityScreenRedirect) => void;
}

const query = graphql`
  query GridMessagingActivityTablesEmbedQuery(
    $dateFrom: Date!
    $dateTo: Date!
    $filterReminders: FilterFindManyReminderInput
  ) {
    ...TableMessagesSent_EmbedQuery @arguments(createdAtFrom: $dateFrom, createdAtTo: $dateTo)
    ...TableMessagesUndelivered_EmbedQuery @arguments(createdAtFrom: $dateFrom, createdAtTo: $dateTo)
    ...TableMessagesReceived_EmbedQuery @arguments(createdAtFrom: $dateFrom, createdAtTo: $dateTo)
    ...TableReminders_EmbedQuery @arguments(count: 5, filter: $filterReminders)
  }
`;

const GridMessagingActivityTables: React.FC<GridMessagingActivityTablesProps> = ({
  dateFrom,
  sendAtFrom,
  dateTo,
  onRedirect,
  ...rest
}) => {
  const data = useLazyLoadQuery<GridMessagingActivityTablesEmbedQuery>(query, {
    dateFrom,
    dateTo,
    filterReminders: {
      _operators: {
        sendAt: { gte: sendAtFrom, lte: dateTo },
      },
    },
  });

  const { formatMessage } = useIntl();
  const environment = useRelayEnvironment();
  const { fetchKey } = useQueryOptions('ChatActivity');

  useEffect(() => {
    if (typeof fetchKey === 'number' && fetchKey % 10 === 0) {
      fetchQuery(environment, query, { dateFrom, sendAtFrom: moment().valueOf(), dateTo });
    }
  }, [fetchKey, environment, dateFrom, dateTo]);

  return (
    <Grid container spacing={24} {...rest}>
      <Grid item xs={12}>
        <Toolbar variant="dense" disableGutters>
          <TypographyWithIcon
            icon={<SvgIconWrapper Icon={FastRewindIcon} color="success" fontSize="small" />}
            variant="body1LargeStrongBold"
          >
            {formatMessage(messages.lastSent)}
          </TypographyWithIcon>
          <ButtonNavigateNext onClick={() => onRedirect('sentMessages')}>
            {formatMessage(messages.redirectSent)}
          </ButtonNavigateNext>
        </Toolbar>
        <Paper elevation={0} square>
          <TableMessagesSent fragmentRef={data} padding="checkbox" />
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Toolbar variant="dense" disableGutters>
          <TypographyWithIcon
            icon={<SvgIconWrapper Icon={FeedbackIcon} color="error" fontSize="small" />}
            variant="body1LargeStrongBold"
          >
            {formatMessage(messages.lastUndelivered)}
          </TypographyWithIcon>
          <ButtonNavigateNext onClick={() => onRedirect('undeliveredMessages')}>
            {formatMessage(messages.redirectUndelivered)}
          </ButtonNavigateNext>
        </Toolbar>
        <Paper elevation={0} square>
          <TableMessagesUndelivered fragmentRef={data} padding="checkbox" />
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Toolbar variant="dense" disableGutters>
          <TypographyWithIcon
            icon={<SvgIconWrapper Icon={ChatIcon} color="info" fontSize="small" />}
            variant="body1LargeStrongBold"
          >
            {formatMessage(messages.lastReceived)}
          </TypographyWithIcon>
          <ButtonNavigateNext onClick={() => onRedirect('receivedMessages')}>
            {formatMessage(messages.redirectReceived)}
          </ButtonNavigateNext>
        </Toolbar>
        <Paper elevation={0} square>
          <TableMessagesReceived fragmentRef={data} padding="checkbox" />
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Toolbar variant="dense" disableGutters>
          <TypographyWithIcon
            icon={<SvgIconWrapper Icon={SubjectIcon} color="default" fontSize="small" />}
            variant="body1LargeStrongBold"
          >
            {formatMessage(messages.nextQueued)}
          </TypographyWithIcon>
          <ButtonNavigateNext onClick={() => onRedirect(MessagingActivityScreenTab.Queued)}>
            {formatMessage(messages.redirectQueued)}
          </ButtonNavigateNext>
        </Toolbar>
        <Paper elevation={0} square>
          <TableReminders
            fragmentRef={data}
            renderEmptyState={() => (
              <EmptyStateTable>{formatMessage(messages.emptyStateNextReminders)}</EmptyStateTable>
            )}
            infiniteScrollProps={{ hasNext: false }}
            padding="dense"
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default (props: GridMessagingActivityTablesProps) => {
  const { formatMessage } = useIntl();

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <Grid container spacing={24}>
            <Grid item xs={12}>
              <Toolbar variant="dense" disableGutters>
                <TypographyWithIcon
                  icon={<SvgIconWrapper Icon={FastRewindIcon} color="success" fontSize="small" />}
                  variant="body1LargeStrongBold"
                >
                  {formatMessage(messages.lastSent)}
                </TypographyWithIcon>
              </Toolbar>
              <Paper elevation={0} square>
                <Table padding="checkbox">
                  <TableHeadMessages />
                  <TableBody>
                    <ContentLoaderTableRows colSpan={tableHeadMessagesCols} />
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        }
      >
        <GridMessagingActivityTables {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};
